import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Figure, Flow } from '../components/Diagram';

const PHASES = [
  {
    label: 'Edit a Draft',
    body:
      'Modules, Bundles, and Variable Components start life as a Draft. Drafts are freely editable and never appear on a customer-facing subscription.',
  },
  {
    label: 'Publish a version',
    body:
      'Publishing freezes the row, stamps an immutable version number, links it to the previous version via previousVersionId (or supersededBy for components), and slots it into the catalog for new subscriptions.',
  },
  {
    label: 'New subscriptions pin the latest',
    body:
      'When a Subscription is created against a Bundle, it stores (bundleId, bundleVersion) at the version that was current at creation time. Same for moduleId/moduleVersion and for each Variable Component in variableComponentRefs.',
  },
  {
    label: 'Existing subscriptions stay where they are',
    body:
      'Publishing a new version does not touch live subscriptions. Their pins do not move. Renewals continue to use the pinned version, with prices already frozen on the subscription itself.',
  },
  {
    label: 'Migrate explicitly, or grandfather forever',
    body:
      'When you do want to move a subscription forward, call migrateSubscriptionModule / migrateSubscriptionBundle / migrateSubscriptionComponent with the target version and a timing (MID_CYCLE_PRORATED applies now and issues an adjustment invoice; NEXT_RENEWAL queues a pendingMigration that applies at the next renewal). bulkMigrateSubscriptions does the same thing for many subscriptions at once. To record that a customer stays where they are, call grandfatherSubscription with a reason — it moves nothing and writes the decision to their plan history, so the pin reads as deliberate rather than neglected.',
  },
];

export function VersioningGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Versioning & grandfathering — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Versioning &amp; grandfathering</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Ship pricing changes without <em>breaking</em> existing customers.
        </h1>
        <p className="dp-oneliner">
          Modules, Bundles, and Variable Components are immutable once Published. Subscriptions
          pin specific versions. Upgrades are explicit. Grandfathering is a recorded decision,
          not an accident.
        </p>
      </header>

      <section className="dp-section">
        <h2>The five phases</h2>
        <ol className="qs-steps">
          {PHASES.map((p, i) => (
            <li className="qs-step" key={p.label}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>{i + 1}</span>
              <div>
                <div className="qs-step-label">{p.label}</div>
                <div className="qs-step-detail">{p.body}</div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="dp-section">
        <h2>What gets pinned where</h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Subscription → Bundle</div>
            <div className="dp-field-v">
              <code>{'{ bundleId, bundleVersion }'}</code> — pinned at creation. Mutually exclusive
              with the Module pin.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Subscription → Module</div>
            <div className="dp-field-v">
              <code>{'{ moduleId, moduleVersion }'}</code> — for subscriptions that buy a single
              module rather than a bundle.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Subscription → Variable Components</div>
            <div className="dp-field-v">
              <code>variableComponentRefs[]</code> — one entry per component, each with
              <code>{' { componentId, componentVersion } '}</code>. Inherited components from
              the bundle/module are flagged via <code>inheritedFromParent</code>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Subscription → Price</div>
            <div className="dp-field-v">
              <code>basePrice</code>, <code>currency</code>, and (optionally)
              <code> exchangeRateUsed</code> are frozen on the subscription at creation. Renewals
              and invoices read from here.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Bundle → Modules</div>
            <div className="dp-field-v">
              <code>moduleRefs[]</code> — each entry pins a specific Module version. Publishing a
              new Bundle version re-pins to whatever Module versions are current at that moment.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Module → Variable Components</div>
            <div className="dp-field-v">
              <code>variableComponentRefs[]</code> — each entry pins a specific Component
              version. Same publish rule applies.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Migrate vs. grandfather</h2>
        <p className="dp-plain">
          <strong>Migrate</strong> when a customer should move to a newer version — for
          example, you&rsquo;ve fixed a pricing error or added a new included quantity that the
          customer should get. <code>migrateSubscriptionModule</code>{' '}
          / <code>migrateSubscriptionBundle</code> / <code>migrateSubscriptionComponent</code>{' '}
          bump the pin and emit an EXPANDED or CONTRACTED lifecycle event with the MRR delta.
          The required <code>timing</code> argument decides what happens to revenue:
          <code> MID_CYCLE_PRORATED</code> applies immediately and issues an adjustment invoice for
          the remainder of the cycle; <code>NEXT_RENEWAL</code> records a{' '}
          <code>pendingMigration</code> that the renewal cron applies and bills cleanly.{' '}
          <code>bulkMigrateSubscriptions</code> does the same for many subscriptions at once;
          per-subscription failures do not block the rest. To clear a queued NEXT_RENEWAL
          before it applies, call <code>cancelPendingMigration</code>.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>Grandfather</strong> when the customer should stay on the old version
          indefinitely — perhaps they have a multi-year contract or you don&rsquo;t want to
          touch their pricing during a sensitive period.
          <code> grandfatherSubscription</code> takes a required <code>reason</code> and
          records the decision on the subscription&rsquo;s plan history. It moves nothing:
          the pins stay exactly where they are, which is the point. From then on the
          subscription reports as grandfathered, <code>subscriptionsWithOutdatedPins</code>{' '}
          leaves it out, and the detail page shows the reason instead of prompting for a
          migration.
        </p>
        <Figure
          title="After a new version is published"
          caption="Existing subscriptions stay pinned until someone migrates them or records a grandfather decision."
        >
          <Flow
            steps={[
              { title: 'New version published', text: 'Immutable, available to new subscriptions.' },
              { title: 'Existing pins stay', text: 'Renewals keep using the pinned version.', tone: 'muted' },
              { fork: [
                { title: 'Migrate, mid-cycle', text: 'Applies now with an adjustment invoice.', tone: 'accent' },
                { title: 'Migrate, next renewal', text: 'Queued as a pending migration.', tone: 'accent' },
                { title: 'Grandfather', text: 'Reason recorded; pins do not move.', tone: 'warn' },
              ] },
            ]}
          />
        </Figure>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          To put a customer on a version that is <em>older</em> than the one they are on —
          or on a version of the plan that predates Revvly entirely — see{' '}
          <Link to="/docs/guides/legacy-pricing">Price a client on an older version</Link>.
        </p>
        <div className="api-note" style={{ marginTop: 18 }}>
          <span className="api-note-icon">i</span>
          <span>
            Neither action is reversible without writing a new lifecycle event. Migrations cannot
            be undone — only superseded by another migration. Grandfathering can be lifted by an
            explicit migration.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <h2>Tracking what&rsquo;s outdated</h2>
        <p className="dp-plain">
          The <code>subscriptionsWithOutdatedPins</code> query returns active subscriptions
          whose Bundle, Module, or Component pins are behind the latest Published versions —
          along with the per-pin diff so you can see which entity drifted and by how many versions.
          It excludes subscriptions whose pin is deliberate — grandfathered, or reconstructed
          history — so the list stays a list of things to do; pass includeGrandfathered to see
          them anyway. Use it as the input to an ops triage queue:
          decide per row whether to migrate or grandfather, then sweep the rest.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Before publishing a new Module / Bundle version, call <code>publishGapCheck</code>{' '}
          to see the per-component, per-currency coverage matrix — publishing 422s if any
          component lacks a price in any supported currency. To debug a single billed line,
          call <code>resolvePriceWalk</code> to see exactly which scope (SUBSCRIPTION / BUNDLE /
          MODULE / GLOBAL_DEFAULT) produced the unit price.
        </p>
      </section>

      <section className="dp-section">
        <h2>Interplay with scoped pricing</h2>
        <p className="dp-plain">
          Variable Component Prices are themselves pinned to a Component <em>version</em>. When a
          Subscription pins Variable Component v1, the price resolver only considers
          Variable Component Prices that target v1. Publishing a new Component version means
          publishing fresh prices for it (you can copy from v1 if the numbers haven&rsquo;t
          changed). This is what lets you ship a new pricing structure without disturbing
          existing customers — they keep paying v1 prices for v1 components.
        </p>
      </section>

      <section className="dp-section">
        <h2>Next up</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/scoped-pricing" style={{ color: 'inherit', textDecoration: 'none' }}>
              Scope-based pricing ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/pricing-models" style={{ color: 'inherit', textDecoration: 'none' }}>
              Pricing models reference ›
            </Link>
          </li>
          <li>
            <Link to="/docs/concepts/subscription" style={{ color: 'inherit', textDecoration: 'none' }}>
              The Subscription concept ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
